import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { getCompany } from '@/lib/company';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash, Printer, FloppyDisk, Calculator } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 3 — Salary Calculation + Printable HTML Slip.
//
// Admin picks a staff + month → live preview from backend (driven by attendance
// + shift settings). Admin tweaks advance / incentives / other deductions /
// payment status, then saves the slip. Saved slips for the month list below
// with a Print button that renders a clean A5 invoice-style slip in a dialog.

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Map of attendance status → colored chip.
const STATUS_CHIPS = {
  present:      'bg-[#DCFCE7] text-[#15803D]',
  half_day:     'bg-[#FFEDD5] text-[#9A3412]',
  absent:       'bg-[#FEE2E2] text-[#B91C1C]',
  weekly_off:   'bg-[#E0E7FF] text-[#4338CA]',
  paid_leave:   'bg-[#EDE9FE] text-[#6D28D9]',
  unpaid_leave: 'bg-[#F4F4F5] text-[#52525B]',
  not_marked:   'bg-[#F4F4F5] text-[#A1A1AA]',
};

// --- Generator card (admin only) ---
function GeneratorCard({ staff, onSaved }) {
  const [userId, setUserId] = useState('');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  // Pre-defined templates for Quick-Apply.
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    api.get('/incentive-templates').then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);
  // Adjustments
  const [advance, setAdvance] = useState(0);
  const [incentives, setIncentives] = useState([]);
  const [otherDed, setOtherDed] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentDate, setPaymentDate] = useState('');
  const [remarks, setRemarks] = useState('');

  const fetchPreview = useCallback(async () => {
    if (!userId || !month) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/salary/preview?user_id=${userId}&month=${month}`);
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [userId, month]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const incTotal = incentives.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const odTotal = otherDed.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const advanceN = parseFloat(advance) || 0;
  const netPayable = preview ? Math.round(((preview.basic_after_auto + incTotal - advanceN - odTotal) + Number.EPSILON) * 100) / 100 : 0;

  const save = async () => {
    if (!preview) return;
    try {
      const { data } = await api.post('/salary', {
        user_id: userId, month,
        advance: advanceN,
        incentives: incentives.filter(i => i.label || i.amount).map(i => ({ label: i.label || '', amount: parseFloat(i.amount) || 0 })),
        other_deductions: otherDed.filter(d => d.label || d.amount).map(d => ({ label: d.label || '', amount: parseFloat(d.amount) || 0 })),
        payment_status: paymentStatus,
        payment_mode: paymentMode,
        payment_date: paymentDate,
        remarks,
      });
      toast.success('Salary slip saved');
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Staff</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="rounded-lg mt-1" data-testid="salary-user"><SelectValue placeholder="Pick staff…" /></SelectTrigger>
              <SelectContent>
                {staff.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}{s.designation ? ` — ${s.designation}` : ''}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Salary Month</Label>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-lg mt-1" data-testid="salary-month" />
          </div>
          <div className="flex items-end">
            <Button onClick={fetchPreview} variant="outline" className="rounded-xl w-full" disabled={!userId || loading} data-testid="recalc-btn">
              <Calculator size={16} className="mr-1.5" /> Recalculate
            </Button>
          </div>
        </div>

        {preview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { k: 'present',      label: 'Present' },
                { k: 'half_day',     label: 'Half Day' },
                { k: 'absent',       label: 'Absent (incl. unmarked)', val: preview.effective_absent },
                { k: 'weekly_off',   label: 'Weekly Off' },
                { k: 'paid_leave',   label: 'Paid Leave', extra: ` / ${preview.paid_leave_limit} limit` },
                { k: 'unpaid_leave', label: 'Unpaid Leave' },
              ].map(c => (
                <div key={c.k} className="border border-[#E4E4E7] rounded-lg p-2.5">
                  <p className={`text-[9px] uppercase tracking-[0.12em] font-semibold inline-block px-1.5 py-0.5 rounded ${STATUS_CHIPS[c.k] || 'text-[#52525B]'}`}>{c.label}</p>
                  <p className="text-xl font-bold text-[#0A0A0A] mt-0.5 font-mono">{c.val ?? preview.counts[c.k]}{c.extra ? <span className="text-[10px] text-[#A1A1AA] font-normal">{c.extra}</span> : null}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#F4F4F5]">
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#15803D]">Earnings</p>
                <div className="flex justify-between text-sm"><span className="text-[#52525B]">Basic ({preview.working_days} days × {fmt(preview.daily_rate)})</span><span className="font-mono font-semibold">{fmt(preview.monthly_salary)}</span></div>
                {incentives.map((it, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Label (e.g. Bonus)" value={it.label} onChange={e => setIncentives(incentives.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-lg h-8 text-xs flex-1" />
                    <Input type="number" placeholder="0" value={it.amount} onChange={e => setIncentives(incentives.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="rounded-lg h-8 text-xs w-28 text-right font-mono" />
                    <button type="button" onClick={() => setIncentives(incentives.filter((_, j) => j !== i))} className="text-[#EF4444] p-1"><Trash size={14} /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#10B981]" onClick={() => setIncentives([...incentives, { label: '', amount: '' }])} data-testid="add-incentive">
                  <Plus size={12} className="mr-1" /> Add Incentive
                </Button>
                {templates.filter(t => t.template_type === 'incentive').length > 0 && (
                  <Select value="" onValueChange={(tplId) => {
                    const t = templates.find(x => x.id === tplId);
                    if (t) setIncentives(prev => [...prev, { label: t.name, amount: t.default_amount }]);
                  }}>
                    <SelectTrigger className="h-7 text-xs rounded-lg mt-1" data-testid="apply-incentive-tpl"><SelectValue placeholder="⚡ Quick-apply template…" /></SelectTrigger>
                    <SelectContent>
                      {templates.filter(t => t.template_type === 'incentive').map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name} · {fmt(t.default_amount)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-[#F4F4F5]"><span className="font-semibold">Total Earnings</span><span className="font-mono font-bold text-[#15803D]">{fmt(preview.monthly_salary + incTotal)}</span></div>
              </div>

              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#B91C1C]">Deductions</p>
                {Object.entries(preview.deductions).filter(([, v]) => (v.amount || 0) > 0).map(([key, v]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-[#52525B]">
                      {key === 'absent_days' && `Absent (${v.days} days)`}
                      {key === 'half_days' && `Half Day (${v.days} days × ½)`}
                      {key === 'unpaid_leave' && `Unpaid Leave (${v.days} days)`}
                      {key === 'paid_leave_overage' && `Extra Paid Leave (${v.days} days)`}
                      {key === 'late_strike' && `Late Strike (${v.strikes} × ½ day)`}
                    </span>
                    <span className="font-mono text-[#B91C1C]">- {fmt(v.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-1 border-t border-dashed border-[#F4F4F5]"><span className="text-[#52525B]">Advance</span>
                  <Input type="number" value={advance} onChange={e => setAdvance(e.target.value)} className="rounded-lg h-7 text-xs w-28 text-right font-mono" data-testid="salary-advance" />
                </div>
                {otherDed.map((it, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Label (e.g. PF)" value={it.label} onChange={e => setOtherDed(otherDed.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="rounded-lg h-8 text-xs flex-1" />
                    <Input type="number" placeholder="0" value={it.amount} onChange={e => setOtherDed(otherDed.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} className="rounded-lg h-8 text-xs w-28 text-right font-mono" />
                    <button type="button" onClick={() => setOtherDed(otherDed.filter((_, j) => j !== i))} className="text-[#EF4444] p-1"><Trash size={14} /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#EF4444]" onClick={() => setOtherDed([...otherDed, { label: '', amount: '' }])} data-testid="add-deduction">
                  <Plus size={12} className="mr-1" /> Add Deduction
                </Button>
                {templates.filter(t => t.template_type === 'deduction').length > 0 && (
                  <Select value="" onValueChange={(tplId) => {
                    const t = templates.find(x => x.id === tplId);
                    if (t) setOtherDed(prev => [...prev, { label: t.name, amount: t.default_amount }]);
                  }}>
                    <SelectTrigger className="h-7 text-xs rounded-lg mt-1" data-testid="apply-deduction-tpl"><SelectValue placeholder="⚡ Quick-apply template…" /></SelectTrigger>
                    <SelectContent>
                      {templates.filter(t => t.template_type === 'deduction').map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name} · {fmt(t.default_amount)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-[#F4F4F5]">
                  <span className="font-semibold">Total Deductions</span>
                  <span className="font-mono font-bold text-[#B91C1C]">- {fmt(preview.auto_deductions_total + advanceN + odTotal)}</span>
                </div>
              </div>
            </div>

            <div className="bg-[#F8FAFC] border border-[#E4E4E7] rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Payment Status</Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="salary-payment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Payment Mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="salary-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Payment Date</Label>
                <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className="rounded-lg mt-1" data-testid="salary-payment-date" />
              </div>
              <div className="text-right md:col-span-1">
                <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Net Payable</p>
                <p className={`text-3xl font-bold font-mono ${netPayable < 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`} data-testid="salary-net">{fmt(netPayable)}</p>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Remarks</Label>
              <Textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} className="rounded-lg mt-1" data-testid="salary-remarks" />
            </div>

            <div className="flex justify-end">
              <Button onClick={save} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-salary-btn">
                <FloppyDisk size={16} className="mr-1.5" /> Save Slip
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- Printable salary slip dialog ---
function SlipDialog({ slip, company, onClose }) {
  if (!slip) return null;
  const b = slip.breakdown || {};
  const monthLabel = new Date(`${slip.month}-01`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const doPrint = () => {
    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) return;
    const html = document.getElementById('printable-slip').outerHTML;
    w.document.write(`<!doctype html><html><head><title>Salary — ${slip.user_name} — ${monthLabel}</title>
      <style>
        body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; color: #0A0A0A; padding: 24px; }
        h1, h2, h3 { margin: 0; }
        table { width: 100%; border-collapse: collapse; }
        td, th { padding: 6px 8px; }
        .border-b { border-bottom: 1px solid #E4E4E7; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #0A0A0A; margin-bottom: 16px; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .text-right { text-align: right; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 16px; }
        .total-row { background: #F8FAFC; border-top: 2px solid #0A0A0A; font-weight: 700; }
        .pill { padding: 2px 8px; border-radius: 9999px; font-size: 11px; }
        .pill.paid { background: #DCFCE7; color: #15803D; }
        .pill.pending { background: #FEF3C7; color: #92400E; }
        @media print { body { padding: 12mm; } }
      </style>
    </head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  const incentives = slip.incentives || [];
  const otherDed = slip.other_deductions || [];
  const deds = b.deductions || {};

  return (
    <Dialog open={!!slip} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="sr-only">
          <DialogTitle>Salary Slip — {slip.user_name} — {monthLabel}</DialogTitle>
          <DialogDescription>Preview of the salary slip. Click Print to send to printer or save as PDF.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-between items-center mb-2">
          <p className="text-xs text-[#52525B]">Preview — click Print to send to printer / save as PDF.</p>
          <Button onClick={doPrint} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="print-slip-btn">
            <Printer size={16} className="mr-1.5" /> Print
          </Button>
        </div>
        <div id="printable-slip" className="bg-white p-6 rounded-lg border border-[#E4E4E7]" data-testid="printable-slip">
          <div className="header" style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #0A0A0A', paddingBottom: 12, marginBottom: 16 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700 }}>{company?.name || 'CSC'}</h1>
              <p style={{ fontSize: 12, color: '#52525B', marginTop: 2 }}>Salary Slip — {monthLabel}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span className={`pill ${slip.payment_status === 'paid' ? 'paid' : 'pending'}`} style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, background: slip.payment_status === 'paid' ? '#DCFCE7' : '#FEF3C7', color: slip.payment_status === 'paid' ? '#15803D' : '#92400E' }}>
                {slip.payment_status === 'paid' ? 'PAID' : 'PENDING'}
              </span>
              {slip.payment_date && <p style={{ fontSize: 11, color: '#52525B', marginTop: 4 }}>Paid {fmtDate(slip.payment_date)} · {slip.payment_mode || '—'}</p>}
            </div>
          </div>

          <table style={{ width: '100%', marginBottom: 16 }}>
            <tbody>
              <tr><td style={{ padding: '4px 0', color: '#52525B', fontSize: 12 }}>Staff</td><td style={{ padding: '4px 0', fontWeight: 600 }}>{slip.user_name}</td></tr>
              <tr><td style={{ padding: '4px 0', color: '#52525B', fontSize: 12 }}>Designation</td><td style={{ padding: '4px 0' }}>{slip.designation || '—'}</td></tr>
              <tr><td style={{ padding: '4px 0', color: '#52525B', fontSize: 12 }}>Calendar / Working Days</td><td style={{ padding: '4px 0' }} className="mono">{b.calendar_days} / {b.working_days}</td></tr>
              <tr><td style={{ padding: '4px 0', color: '#52525B', fontSize: 12 }}>Daily Rate</td><td style={{ padding: '4px 0' }} className="mono">{fmt(b.daily_rate)}</td></tr>
            </tbody>
          </table>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E4E4E7' }}>
                <th style={{ textAlign: 'left', padding: '6px 4px', fontSize: 10, textTransform: 'uppercase', color: '#52525B', letterSpacing: 1 }}>Earning</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', fontSize: 10, textTransform: 'uppercase', color: '#52525B', letterSpacing: 1 }}>Amount</th>
                <th style={{ textAlign: 'left', padding: '6px 4px', fontSize: 10, textTransform: 'uppercase', color: '#52525B', letterSpacing: 1 }}>Deduction</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', fontSize: 10, textTransform: 'uppercase', color: '#52525B', letterSpacing: 1 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                <td style={{ padding: '6px 4px' }}>Basic Salary</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(b.monthly_salary)}</td>
                <td style={{ padding: '6px 4px' }}>Absent / Unmarked ({deds.absent_days?.days || 0} days)</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(deds.absent_days?.amount || 0)}</td>
              </tr>
              {incentives.length > 0 ? incentives.map((i, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #F4F4F5' }}>
                  <td style={{ padding: '6px 4px' }}>{i.label || `Incentive ${idx + 1}`}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(i.amount)}</td>
                  {idx === 0 ? (
                    <>
                      <td style={{ padding: '6px 4px' }}>Half Day ({deds.half_days?.days || 0} days × ½)</td>
                      <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(deds.half_days?.amount || 0)}</td>
                    </>
                  ) : <><td/><td/></>}
                </tr>
              )) : (
                <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                  <td style={{ padding: '6px 4px', color: '#A1A1AA' }}>—</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: '#A1A1AA' }}>—</td>
                  <td style={{ padding: '6px 4px' }}>Half Day ({deds.half_days?.days || 0} days × ½)</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(deds.half_days?.amount || 0)}</td>
                </tr>
              )}
              <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                <td/>
                <td/>
                <td style={{ padding: '6px 4px' }}>Unpaid Leave ({deds.unpaid_leave?.days || 0} days)</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(deds.unpaid_leave?.amount || 0)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                <td/>
                <td/>
                <td style={{ padding: '6px 4px' }}>Extra Paid Leave ({deds.paid_leave_overage?.days || 0} days)</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(deds.paid_leave_overage?.amount || 0)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                <td/>
                <td/>
                <td style={{ padding: '6px 4px' }}>Late Strike ({deds.late_strike?.strikes || 0} × ½ day)</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(deds.late_strike?.amount || 0)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #F4F4F5' }}>
                <td/>
                <td/>
                <td style={{ padding: '6px 4px' }}>Advance</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(slip.advance)}</td>
              </tr>
              {otherDed.map((d, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #F4F4F5' }}>
                  <td/>
                  <td/>
                  <td style={{ padding: '6px 4px' }}>{d.label || `Deduction ${idx + 1}`}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }} className="mono">{fmt(d.amount)}</td>
                </tr>
              ))}
              <tr className="total-row" style={{ background: '#F8FAFC', borderTop: '2px solid #0A0A0A' }}>
                <td style={{ padding: '8px 4px', fontWeight: 700 }}>Total Earnings</td>
                <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700 }} className="mono">{fmt(b.monthly_salary + (slip.incentives_total || 0))}</td>
                <td style={{ padding: '8px 4px', fontWeight: 700 }}>Total Deductions</td>
                <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700 }} className="mono">{fmt((b.auto_deductions_total || 0) + (slip.advance || 0) + (slip.other_deductions_total || 0))}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ background: '#F8FAFC', border: '1px solid #E4E4E7', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>NET PAYABLE</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: slip.net_payable < 0 ? '#EF4444' : '#10B981' }} className="mono">{fmt(slip.net_payable)}</span>
          </div>

          {slip.remarks && (
            <div style={{ marginTop: 16, fontSize: 12, color: '#52525B' }}>
              <strong>Remarks:</strong> {slip.remarks}
            </div>
          )}

          <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #E4E4E7', paddingTop: 16 }}>
            <div style={{ textAlign: 'center', width: 200 }}>
              <div style={{ borderTop: '1px solid #0A0A0A', paddingTop: 4 }}>
                <p style={{ fontSize: 11, color: '#52525B' }}>Employee Signature</p>
              </div>
            </div>
            <div style={{ textAlign: 'center', width: 200 }}>
              <div style={{ borderTop: '1px solid #0A0A0A', paddingTop: 4 }}>
                <p style={{ fontSize: 11, color: '#52525B' }}>For {company?.name || 'CSC'}</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SalaryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [staff, setStaff] = useState([]);
  const [slips, setSlips] = useState([]);
  const [company, setCompany] = useState({});
  const [slipTarget, setSlipTarget] = useState(null);
  const [filterMonth, setFilterMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const refresh = useCallback(() => {
    api.get('/salary').then(r => setSlips(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.get('/users').then(r => setStaff(r.data.filter(u => u.active !== false))).catch(() => {});
    }
    getCompany().then(setCompany).catch(() => {});
    refresh();
  }, [isAdmin, refresh]);

  const filteredSlips = filterMonth ? slips.filter(s => s.month === filterMonth) : slips;

  const remove = async (s) => {
    if (!window.confirm(`Delete salary slip for ${s.user_name} — ${s.month}?`)) return;
    try {
      await api.delete(`/salary/${s.id}`);
      toast.success('Slip deleted');
      refresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="salary-page">
      <p className="text-xs text-[#52525B]">
        {isAdmin
          ? 'Pick a staff + month to auto-calculate from attendance. Adjust advance/incentives/deductions, then save & print the slip.'
          : 'View your monthly salary slips below. Slips are generated by admin.'}
      </p>

      {isAdmin && <GeneratorCard staff={staff} onSaved={() => { refresh(); }} />}

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <p className="text-sm font-semibold text-[#0A0A0A]">Saved Slips</p>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Month</Label>
              <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="rounded-lg w-40 h-8 text-xs" data-testid="filter-month" />
            </div>
          </div>

          {filteredSlips.length === 0 ? (
            <p className="text-sm text-[#52525B] text-center py-6">No slips for this month yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Staff</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Month</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Basic</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Deductions</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Net</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSlips.map(s => (
                  <TableRow key={s.id} className="border-[#E4E4E7]" data-testid={`slip-row-${s.id}`}>
                    <TableCell className="text-sm font-medium">{s.user_name}<p className="text-[10px] text-[#A1A1AA]">{s.designation || ''}</p></TableCell>
                    <TableCell className="text-xs font-mono">{new Date(`${s.month}-01`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmt(s.breakdown?.monthly_salary || 0)}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-[#B91C1C]">- {fmt((s.breakdown?.auto_deductions_total || 0) + (s.advance || 0) + (s.other_deductions_total || 0))}</TableCell>
                    <TableCell className={`text-sm text-right font-mono font-bold ${s.net_payable < 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(s.net_payable)}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-md text-[10px] ${s.payment_status === 'paid' ? 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' : 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]'}`} variant="outline">
                        {s.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]" onClick={() => setSlipTarget(s)} data-testid={`view-slip-${s.id}`}>
                          <Printer size={12} className="mr-1" /> Slip
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#EF4444]" onClick={() => remove(s)} data-testid={`delete-slip-${s.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SlipDialog slip={slipTarget} company={company} onClose={() => setSlipTarget(null)} />
    </div>
  );
}
